// src/pages/Provider/CarCatalogProvider.jsx
import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API_DOMAIN } from "config";
import { Heading, Img } from "../../components";
import CarCard from "../../components/CarCard";
import FilterBar from "components/FilterBar";
import CarsTable from "components/CarsTable";
import useDisplayMode from "useDisplayMode";
import useInfiniteScroll from "components/UseInfiniteScroll";

const tabs = [
  "מודעות בארכיון",
  "כל המודעות",
  "מודעות מאושרות",
    "מודעות במכרז",
  "מודעות עם הצעת מחיר",
  "מודעות שממתינות להצעה",
];

const TAB_FILTERS = {
  "כל המודעות": {},
  "מודעות שממתינות להצעה": { noOffersOnly: 1 },
  "מודעות עם הצעת מחיר": { pendingOnly: 1 },
  "מודעות מאושרות": { approvedOtherOnly: 1 },
  "מודעות בארכיון": { archiveOnly: 1 },
  "מודעות במכרז": { auctionOnly: 1 },
};

export default function CarCatalogProvider({ currentUser, setGlobalCounters, adminMode = false }) {
  const [activeTab, setActiveTab] = useState("מודעות שממתינות להצעה");
  const [displayMode, setDisplayMode] = useDisplayMode();

  // ✅ הוספה קטנה: כמו בדילר
  const handleDisplayMode = (mode) => {
    setDisplayMode(mode);
    localStorage.setItem("displayMode", mode);
  };

  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");
  const [providerId, setProviderId] = useState("");
  const [providers, setProviders] = useState([]);
  const [cars, setCars] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasMore, setHasMore] = useState(true);

  const PAGE_SIZE = 8;

  // ✅ מונע "הבהובים" עקב תשובות מאחרות
  const reqIdRef = useRef(0);
  const abortRef = useRef(null);
  const loadingRef = useRef(false);
  const nextPageRef = useRef(1);

  // ✅ Lazy Load: מביא עמוד, ומוסיף/מאפס לפי reset
  const fetchCars = useCallback(
    async (pageToLoad = 1, reset = false) => {
      if (!currentUser?.id || (!reset && loadingRef.current)) return;

      if (reset) {
        abortRef.current?.abort();
        nextPageRef.current = 1;
        setCars([]);
        setHasMore(true);
      }

      const myReqId = ++reqIdRef.current;
      const controller = new AbortController();
      abortRef.current = controller;
      loadingRef.current = true;
      setLoading(true);
      setError("");

      const params = {
        ...filters,
        ...(adminMode ? { search: search.trim() || undefined, providerId: providerId || undefined } : {
          userId: currentUser.id,
          ownerOnly: 1,
        }),
        ...TAB_FILTERS[activeTab],

        // ✅ Lazy
        page: pageToLoad,
        pageSize: PAGE_SIZE,
      };

      try {
        const endpoint = adminMode ? "/admin/providerCars" : "/car/filterProviderCars";
        const { data } = await axios.get(`${API_DOMAIN}${endpoint}`, {
          params,
          signal: controller.signal,
          withCredentials: adminMode,
        });

        // אם בינתיים יצאה בקשה חדשה יותר – מתעלמים מהישנה
        if (myReqId !== reqIdRef.current) return;

        const list = Array.isArray(data) ? data : [];

        setCars((prev) => {
          if (reset) return list;
          const seen = new Set(prev.map((car) => String(car.id)));
          return [...prev, ...list.filter((car) => !seen.has(String(car.id)))];
        });
        setHasMore(list.length === PAGE_SIZE);
        nextPageRef.current = pageToLoad + 1;
      } catch (err) {
        if (myReqId !== reqIdRef.current) return;
        if (axios.isCancel(err) || err?.code === "ERR_CANCELED") return;
        console.error("שגיאת שליפת רכבים (Provider):", err);
        setError(adminMode ? "לא ניתן לטעון את רכבי הספקים. יש לוודא שהחיבור כמנהל עדיין פעיל." : "לא ניתן לטעון את הרכבים.");
        if (reset) setCars([]);
        setHasMore(false);
      } finally {
        if (myReqId === reqIdRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [currentUser?.id, filters, activeTab, adminMode, search, providerId]
  );

  // ✅ sentinel: רק אם לא טוען ויש עוד
  const sentinelRef = useInfiniteScroll(
    () => {
      if (loadingRef.current || !hasMore) return;
      fetchCars(nextPageRef.current, false);
    },
    { enabled: !loading && hasMore, rootMargin: "0px 0px 240px 0px" }
  );

  // ✅ RESET מלא כשמשנים פילטר/טאב
  useEffect(() => {
    if (!currentUser?.id) return;

    fetchCars(1, true);
    return () => abortRef.current?.abort();
  }, [filters, activeTab, displayMode, currentUser?.id, fetchCars]);

  useEffect(() => {
    if (!adminMode) return;
    Promise.all([
      axios.get(`${API_DOMAIN}/admin/getUsers`, { params: { type: "providers" }, withCredentials: true }),
      axios.get(`${API_DOMAIN}/admin/getUsers`, { params: { type: "privates" }, withCredentials: true }),
    ]).then((responses) => {
      const rows = responses.flatMap(({ data }) => Array.isArray(data) ? data : []);
      setProviders(rows);
    }).catch(() => setProviders([]));
  }, [adminMode]);

  useEffect(() => {
    if (!adminMode || typeof setGlobalCounters !== "function") return;

    const options = { withCredentials: true };
    Promise.all([
      axios.get(`${API_DOMAIN}/admin/providerCars`, options),
      axios.get(`${API_DOMAIN}/admin/providerCars`, { ...options, params: { archiveOnly: 1 } }),
      axios.get(`${API_DOMAIN}/admin/providerCars`, { ...options, params: { auctionOnly: 1 } }),
    ]).then(([regularRes, archiveRes, auctionRes]) => {
      const list = Array.isArray(regularRes.data) ? regularRes.data : [];
      const archiveList = Array.isArray(archiveRes.data) ? archiveRes.data : [];
      const auctionList = Array.isArray(auctionRes.data) ? auctionRes.data : [];
      const auctionFuture = auctionList.filter((car) => {
        const start = car.auction_start_datetime ? new Date(car.auction_start_datetime).getTime() : 0;
        return start > Date.now();
      }).length;

      setGlobalCounters({
        all: list.length,
        noOffers: list.filter((car) => Number(car.offer_status) === 0).length,
        dismantling: list.filter((car) => Number(car.sale_type_id) === 1).length,
        rehabilitation: list.filter((car) => Number(car.sale_type_id) === 2).length,
        pending: list.filter((car) => Number(car.offer_status) === 1).length,
        approved: list.filter((car) => Number(car.offer_status) === 2).length,
        archive: archiveList.length,
        auction: Math.max(0, auctionList.length - auctionFuture),
        auction_future: auctionFuture,
      });
    }).catch((err) => {
      console.error("שגיאת מוני רכבים למנהל:", err);
      setGlobalCounters({ all: 0, noOffers: 0, dismantling: 0, rehabilitation: 0, pending: 0, approved: 0, archive: 0, auction: 0, auction_future: 0 });
    });
  }, [adminMode, setGlobalCounters]);

  // ✅ Counters (לא נוגעים ב-cars כאן!)
  useEffect(() => {
    if (adminMode || typeof setGlobalCounters !== "function") return;
    if (!currentUser?.id) return;

    const baseParams = {
      userId: currentUser.id,
      ownerOnly: 1,
      // בלי page/pageSize כדי לקבל הכל למונים
    };

    Promise.all([
      axios.get(`${API_DOMAIN}/car/filterProviderCars`, { params: baseParams }),
      axios.get(`${API_DOMAIN}/car/filterProviderCars`, { params: { ...baseParams, auctionOnly: 1 } }),
    ])
      .then(([regularRes, auctionRes]) => {
        const list = Array.isArray(regularRes.data) ? regularRes.data : [];
        const auctionList = Array.isArray(auctionRes.data) ? auctionRes.data : [];
        const auction_future = auctionList.filter(c => {
          const start = c.auction_start_datetime ? new Date(c.auction_start_datetime).getTime() : 0;
          return start > Date.now();
        }).length;
        setGlobalCounters({
          all: list.length,
          noOffers: list.filter((c) => Number(c.offer_status) === 0 && Number(c.is_archive) !== 1).length,
          dismantling: list.filter((c) => Number(c.sale_type_id) === 1).length,
          rehabilitation: list.filter((c) => Number(c.sale_type_id) === 2).length,
          pending: list.filter((c) => Number(c.offer_status) === 1).length,
          approved: list.filter((c) => Number(c.offer_status) === 2).length,
          archive: list.filter((c) => Number(c.is_archive) === 1).length,
          auction: auctionList.length - auction_future,
          auction_future,
        });
      })
      .catch((err) => {
        console.error("שגיאת מונים (Provider):", err);
        setGlobalCounters({ all: 0, noOffers: 0, dismantling: 0, rehabilitation: 0, pending: 0, approved: 0, archive: 0, auction: 0 });
      });
  }, [adminMode, currentUser?.id, setGlobalCounters]);

  const providerLabel = adminMode ? "מנהל" : (currentUser?.provider_type === "private" ? "מוכר פרטי" : "ספק");

  return (
    <>
      <div className="car-catalog-dealer flex bg-blue_gray-50 p-8 sm:p-4" style={{ paddingTop: "1rem" }}>
        <div className="w-[100%] items-start gap-4 md:flex-col" style={{ gap: "2%" }}>
<div
  className="main-section flex relative flex-1 flex-col gap-8 self-center rounded-[24px] bg-secondary-primary_white p-6 md:self-stretch sm:p-4"
  style={{ minHeight: "95.5vh" }}
>            <header className="flex flex-col items-start gap-3">
     



    <div style={{width: '9%'}} className="ml-3 w-[9%] absolute top-0 left-0 h-8  flex items-center justify-center gap-4 rounded-br-[10px] md:px-5">
                <div className="flex w-full justify-center gap-1">
                  <Heading size="headinglg" as="h2" className="text-[12px] font-bold !text-gray-900_04">
                    <span className="text-gray-900_04">&nbsp;</span>
                    <span className="font-heebo font-medium text-gray-600">רכבים</span>
                  </Heading>
                  <Img src="/images/img_signal_red_a700_03_16x16.svg" alt="Signal" className="h-[16px]" />
                </div>
                <div className="flex w-full items-center justify-center">
                  <Heading size="textxs" as="h3" className="!font-heebo text-[12px] font-medium !text-gray-600">
                                        {providerLabel}

                  </Heading>
                  <Img src="/images/img_vector_deep_orange_500.svg" alt="Image" className="h-[16px]" />
                </div>
              </div>
              <div className="flex items-center justify-between gap-5 mt-5 self-stretch border-b border-solid border-gray-200 md:flex-col">
                
                {/* ✅ תיקון 1: אייקונים כמו בדילר (כרטיסים/שורות) */}
                <div className="flex w-[6%] justify-center gap-3.5 md:w-full hide-mobile">
                  <Img
                    src="/images/img_button1_gray_100_01.svg"
                    alt="Buttonone"
                    onClick={() => handleDisplayMode("cards")}
                    className="h-[32px] rounded-[10px] cursor-pointer"
                  />
                  <div className="flex items-center rounded-[10px] bg-gray-100_03 px-1.5 py-2.5">
                    <Img
                      src="/images/img_megaphone_gray_600.svg"
                      onClick={() => handleDisplayMode("lines")}
                      alt="Megaphone"
                      className="h-[12px] cursor-pointer"
                    />
                  </div>
                </div>

                {/* ✅ תיקון 2: טאבים באמצע */}
    <div className="flex justify-center gap-4 xs-flex-row-reverse md:flex-row">
                  {tabs.map((tab) => (
                    <div
                      key={tab}
                      style={{
                        marginRight: '0.4%',
                        marginLeft: '0.5%',
                        textAlign: 'center',
                        ...(activeTab === tab ? { borderBottomColor: "#CE0000" } : {}),
                      }}
                      onClick={() => setActiveTab(tab)}
                      className={`flex py-3 cursor-pointer ${
                        activeTab === tab ? "border-b-2" : ""
                      }`}
                    >
                      <Heading
                        as="h6"
                        className={`text-[16px] font-semibold t ${
                          activeTab === tab ? "text-black" : "!text-gray-600"
                        }`}
                      >
                        {tab}
                      </Heading>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3">
                  <Heading size="heading9xl" as="h4" className="!font-heebo text-[25px] font-bold lg:text-[21px]">
                    {adminMode ? "רכבים" : "מודעות רכבים שלי"}
                  </Heading>
                  <Img src="/images/img_car_red_a700_03_1.svg" alt="Car" className="h-[36px]" />
                </div>
              </div>
            </header>

            <div className="flex flex-col gap-6">
              {adminMode && (
                <div className="flex flex-wrap items-center gap-3">
<input
  value={search}
  onChange={(event) => setSearch(event.target.value)}
  placeholder="חיפוש לפי רכב"
  aria-label="חיפוש רכבים"
  className="min-w-[260px] rounded-xl border border-gray-200 bg-white px-4 py-2 outline-none transition-colors hover:border-gray-400 focus:!border-red-700 focus:!outline-none focus:!ring-0 focus:!shadow-none focus-visible:!outline-none focus-visible:!ring-0"
/>
           <select
  value={providerId}
  onChange={(event) => setProviderId(event.target.value)}
  aria-label="סינון לפי ספק"
  dir="rtl"
  style={{ textAlign: "right" }}
  className="user-role-select min-w-[220px] rounded-xl border border-gray-200 bg-white py-2 pl-10 pr-4 outline-none transition-colors hover:border-gray-400 focus:!border-red-700"
>
  <option value="">כל הספקים</option>
  {providers.map((provider) => (
    <option key={provider.id} value={provider.id}>
      {provider.business_name || provider.name}
    </option>
  ))}
</select>
                </div>
              )}
              <FilterBar onChange={setFilters} />

              {displayMode === "cards" && (
<div
  dir="rtl"
  className="grid grid-cols-4 justify-center gap-5 gap-y-5 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1"
>                  <Suspense fallback={<div>Loading feed...</div>}>
                    {cars.map((car, index) => (
                      <CarCard
                        key={`${car.id}-${index}`}
                        currentUser={currentUser}
                        showProviderInfo={adminMode}
                        {...car}
                        action={adminMode ? "לצפייה במודעה" : car.action}
                      />
                    ))}
                    <div ref={sentinelRef} style={{ height: 1 }} />
                  </Suspense>
                </div>
              )}

              {displayMode === "lines" && (
                <>
                  <CarsTable cars={cars} currentUser={currentUser} showProviderInfo={adminMode} />
                  <div ref={sentinelRef} style={{ height: 1 }} />
                </>
              )}

              {error && <div role="alert" className="rounded-lg bg-red-50 p-3 text-red-700">{error}</div>}
           {loading && cars.length === 0 && (
  <div
    className="w-full flex-1 flex items-center justify-center"
    style={{ direction: "rtl", paddingTop: "96px" }}
  >
    <div className="rounded-[16px] border border-gray-200 bg-white px-10 py-8 flex items-center gap-6">
      <div className="flex items-center gap-4" style={{ direction: "ltr" }}>
        <Img
          src="/images/img_car_red_a700_03_1.svg"
          alt="car"
          className="h-[28px] w-[28px]"
        />
        <div className="flex flex-col items-center text-center" style={{ direction: "rtl" }}>
          <Heading
            as="h3"
            className="!font-assistant text-[16px] font-medium !text-gray-900_04"
          >
            טוען רכבים
          </Heading>
          <Heading
            as="h3"
            className="!font-assistant text-[16px] font-medium !text-gray-900_04"
          >
            נא להמתין
          </Heading>
        </div>
      </div>
    </div>
  </div>
)}
              {!hasMore && cars.length > 0 && <div style={{ padding: 8 }}>אין עוד רכבים לטעינה</div>}
{!loading && cars.length === 0 && (
  <div
    className="w-full flex-1 flex items-center justify-center"
    style={{ direction: "rtl", paddingTop: "96px" }}
  >
    <div className="rounded-[16px] border border-gray-200 bg-white px-10 py-8 flex items-center gap-6">
      <div className="flex items-center gap-4" style={{ direction: "ltr" }}>
        <Img
          src="/images/img_car_red_a700_03_1.svg"
          alt="car"
          className="h-[28px] w-[28px]"
        />
        <div className="flex flex-col items-center text-center" style={{ direction: "rtl" }}>
          <Heading
            as="h3"
            className="!font-assistant text-[16px] font-medium !text-gray-900_04"
          >
            אין כרגע תוצאות
          </Heading>
          <Heading
            as="h3"
            className="!font-assistant text-[16px] font-medium !text-gray-900_04"
          >
            חפש משהו אחר
          </Heading>
        </div>
      </div>
    </div>
  </div>
)}            </div>
          </div>
        </div>
      </div>
    </>
  );
}
